import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import { serverConfig } from "../utils/serverConstants";

const API_BASE = serverConfig.url.replace(/\/v3\/?$/, "/v3/support-bot");
const BACKEND_BASE = serverConfig.url.replace(/\/v3\/?$/, "");
// Primary: served from frontend public folder (works in dev without backend running).
// process.env.PUBLIC_URL is "" in dev and "/v2" in production build.
// Fallback: backend static URL.
const BOT_ICON_URL = `${process.env.PUBLIC_URL || ""}/technical-support.png`;
const BOT_ICON_FALLBACK = `${BACKEND_BASE}/technical-support.png`;

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  // Floating button
  floatingBtn: {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "transparent",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.08)",
    },
  },
  // Tooltip callout
  tooltip: {
    position: "fixed",
    bottom: 70,
    right: 68,
    background: "#333",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 500,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    zIndex: 9998,
    animation: "$tooltipPulse 2s ease-in-out infinite",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -6,
      right: 12,
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: "6px solid #333",
    },
  },
  "@keyframes tooltipPulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.7 },
  },
  // Chat panel
  chatPanel: {
    position: "fixed",
    bottom: 90,
    right: 24,
    width: 400,
    height: 500,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
    overflow: "hidden",
    "@media (max-width: 480px)": {
      width: "calc(100vw - 16px)",
      height: "calc(100vh - 120px)",
      right: 8,
      bottom: 80,
      borderRadius: 8,
    },
  },
  // Header
  chatHeader: {
    background: "#1a73e8",
    color: "#fff",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: 20,
    padding: 4,
    lineHeight: 1,
    opacity: 0.8,
    "&:hover": { opacity: 1 },
  },
  // Messages area
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    background: "#f8f9fa",
  },
  // Message bubbles
  msgBot: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: "12px 12px 12px 2px",
    background: "#fff",
    color: "#333",
    fontSize: 13.5,
    lineHeight: 1.5,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    alignSelf: "flex-start",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    userSelect: "text",
    "& a": {
      color: "#1a73e8",
      wordBreak: "break-all",
    },
  },
  msgUser: {
    maxWidth: "80%",
    padding: "10px 14px",
    borderRadius: "12px 12px 2px 12px",
    background: "#1a73e8",
    color: "#fff",
    fontSize: 13.5,
    lineHeight: 1.5,
    alignSelf: "flex-end",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    userSelect: "text",
  },
  // Quick replies
  quickReplies: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
    alignSelf: "flex-start",
  },
  quickReplyBtn: {
    padding: "6px 14px",
    borderRadius: 16,
    border: "1px solid #1a73e8",
    background: "#fff",
    color: "#1a73e8",
    fontSize: 12.5,
    cursor: "pointer",
    transition: "all 0.15s",
    "&:hover": {
      background: "#1a73e8",
      color: "#fff",
    },
  },
  // Input area
  inputArea: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    borderTop: "1px solid #e0e0e0",
    background: "#fff",
    flexShrink: 0,
  },
  inputField: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 20,
    border: "1px solid #ddd",
    outline: "none",
    fontSize: 13.5,
    "&:focus": {
      borderColor: "#1a73e8",
    },
  },
  sendBtn: {
    marginLeft: 8,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "#1a73e8",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "&:hover": { background: "#1557b0" },
    "&:disabled": { background: "#ccc", cursor: "default" },
  },
  // Typing indicator
  typing: {
    alignSelf: "flex-start",
    padding: "8px 14px",
    borderRadius: "12px 12px 12px 2px",
    background: "#fff",
    color: "#999",
    fontSize: 13,
    fontStyle: "italic",
  },
  // Ticket card
  ticketCard: {
    padding: 12,
    borderRadius: 8,
    background: "#e8f5e9",
    border: "1px solid #c8e6c9",
    alignSelf: "flex-start",
    maxWidth: "85%",
    fontSize: 13,
    lineHeight: 1.6,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    userSelect: "text",
  },
  ticketRef: {
    fontWeight: 700,
    color: "#2e7d32",
    fontSize: 14,
  },
  // Status card
  statusCard: {
    padding: 12,
    borderRadius: 8,
    background: "#fff3e0",
    border: "1px solid #ffe0b2",
    alignSelf: "flex-start",
    maxWidth: "85%",
    fontSize: 13,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    userSelect: "text",
    "& a": {
      color: "#1a73e8",
      wordBreak: "break-all",
    },
  },
  // Magic link button
  magicLinkBtn: {
    display: "block",
    width: "100%",
    padding: "10px 16px",
    marginTop: 10,
    borderRadius: 6,
    border: "none",
    background: "#1a73e8",
    color: "#ffffff !important",
    fontSize: 14,
    fontWeight: 600,
    textAlign: "center",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      background: "#1557b0",
      color: "#ffffff !important",
    },
  },
  // Support email card
  supportEmailCard: {
    padding: 12,
    borderRadius: 8,
    background: "#fff",
    border: "1px solid #e0e0e0",
    alignSelf: "flex-start",
    maxWidth: "85%",
    fontSize: 13,
    lineHeight: 1.6,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    userSelect: "text",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
  emailBtn: {
    display: "block",
    width: "100%",
    padding: "10px 16px",
    marginTop: 12,
    borderRadius: 6,
    border: "none",
    background: "#1a73e8",
    color: "#ffffff !important",
    fontSize: 14,
    fontWeight: 600,
    textAlign: "center",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      background: "#1557b0",
      color: "#ffffff !important",
    },
  },
  // Feedback area
  feedbackArea: {
    alignSelf: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
  starRow: {
    display: "flex",
    gap: 4,
  },
  star: {
    cursor: "pointer",
    fontSize: 22,
    color: "#ddd",
    transition: "color 0.15s",
    background: "none",
    border: "none",
    padding: 0,
  },
  starActive: {
    color: "#ffc107",
  },
}));

// ─── Helper: Generate session ID ─────────────────────────────────────────────

function generateSessionId() {
  return "sb-" + Date.now() + "-" + Math.random().toString(36).substring(2, 9);
}

// ─── Simple Markdown-like rendering ──────────────────────────────────────────

function renderText(text) {
  if (!text) return null;
  // Escape HTML first to prevent XSS
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Bold: **text**
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Auto-link URLs
  html = html.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  // Newlines to <br/>
  html = html.replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const BotIcon = ({ size = 22 }) => (
  <img
    src={BOT_ICON_URL}
    alt="Riya"
    width={size}
    height={size}
    style={{ borderRadius: "50%", objectFit: "cover" }}
    onError={(e) => { e.target.onerror = null; e.target.src = BOT_ICON_FALLBACK; }}
  />
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────

export default function SupportBot({ authenticated = false, user = null }) {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const [context, setContext] = useState({
    intent: null,
    flowStep: 0,
    collectedInfo: {},
    authenticated,
    userId: user?.userid || null,
    firstName: user?.firstname || null,
    inHandoff: false,
    handoffStep: 0,
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showTooltip, setShowTooltip] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-dismiss tooltip after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  // Hide tooltip when chat opens
  useEffect(() => {
    if (isOpen) setShowTooltip(false);
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load welcome message on first open
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;
      sendMessage("");
    }
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ─── API Calls ─────────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text) => {
      setIsLoading(true);

      // Add user message to chat (skip for empty initial)
      if (text) {
        setMessages((prev) => [...prev, { sender: "user", text }]);
        setConversationHistory((prev) => [...prev, { sender: "user", text }]);
      }

      try {
        const res = await axios.post(`${API_BASE}/message`, {
          sessionId,
          message: text,
          context,
        });

        const data = res.data;

        if (data.context) {
          setContext((prev) => ({ ...prev, ...data.context }));
        }

        // Handle ticket creation flow
        if (data.type === "create_ticket") {
          await createTicket(data.ticketData);
          return;
        }

        // Add bot response
        if (data.reply) {
          const botMsg = {
            sender: "bot",
            text: data.reply,
            type: data.type || "text",
            quickReplies: data.quickReplies || [],
            magicLinkUrl: data.magicLinkUrl || null,
          };
          setMessages((prev) => [...prev, botMsg]);
          setConversationHistory((prev) => [
            ...prev,
            { sender: "bot", text: data.reply },
          ]);
        }
      } catch (err) {
        console.error("Support bot error:", err);
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "Sorry, I'm having trouble connecting. Please try again.",
            type: "text",
            quickReplies: [],
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, context]
  );

  const createTicket = async (ticketData) => {
    try {
      const res = await axios.post(`${API_BASE}/create-ticket`, {
        ...ticketData,
        sessionId,
        conversationTranscript: conversationHistory,
      });

      const data = res.data;

      if (data.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            type: "handoff",
            ticketRef: data.ticketRef,
            priority: data.priority,
            text: data.message,
          },
        ]);

        // Reset context and show feedback
        setContext({
          intent: null,
          flowStep: 0,
          collectedInfo: {},
          authenticated,
          userId: user?.userid || null,
          firstName: user?.firstname || null,
          inHandoff: false,
          handoffStep: 0,
        });

        setTimeout(() => setShowFeedback(true), 1000);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "Sorry, I couldn't create the ticket. Please try emailing support@assesshub.com directly.",
            type: "text",
            quickReplies: [],
          },
        ]);
      }
    } catch (err) {
      console.error("Create ticket error:", err);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Sorry, I couldn't create the ticket. Please try emailing support@assesshub.com directly.",
          type: "text",
          quickReplies: [],
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const submitFeedback = async (rating) => {
    setFeedbackRating(rating);
    try {
      await axios.post(`${API_BASE}/feedback`, {
        sessionId,
        rating,
      });
      setFeedbackSent(true);
    } catch (err) {
      console.error("Feedback error:", err);
      setFeedbackSent(true);
    }
  };

  // ─── Event Handlers ────────────────────────────────────────────────────────

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || isLoading) return;
    setInputText("");
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (text) => {
    if (isLoading) return;
    sendMessage(text);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Tooltip callout */}
      {showTooltip && !isOpen && (
        <div className={classes.tooltip}>
          Need help? Click here for support
        </div>
      )}

      {/* Floating Button */}
      <button
        className={classes.floatingBtn}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close support chat" : "Ask Riya"}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <BotIcon size={36} />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className={classes.chatPanel}>
          {/* Header */}
          <div className={classes.chatHeader}>
            <span className={classes.headerTitle}>
              <BotIcon size={20} /> Ask Riya
            </span>
            <button
              className={classes.closeBtn}
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>

          {/* Messages */}
          <div className={classes.messagesArea}>
            {messages.map((msg, idx) => {
              if (msg.sender === "user") {
                return (
                  <div key={idx} className={classes.msgUser}>
                    {msg.text}
                  </div>
                );
              }

              // Bot messages
              if (msg.type === "support_email") {
                return (
                  <React.Fragment key={idx}>
                    <div className={classes.supportEmailCard}>
                      {renderText(msg.text)}
                      {renderText(
                        'Please send an email to **support@assesshub.com** with the following details:\n\n' +
                        '- **Login ID / Email**\n' +
                        '- **Organization Name**\n' +
                        '- **Issue Faced**\n' +
                        '- **Error Screenshot** (please attach)'
                      )}
                    </div>
                    {msg.quickReplies && msg.quickReplies.length > 0 && (
                      <div className={classes.quickReplies}>
                        {msg.quickReplies.map((qr, qrIdx) => (
                          <button
                            key={qrIdx}
                            className={classes.quickReplyBtn}
                            onClick={() => handleQuickReply(qr)}
                          >
                            {qr}
                          </button>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              }

              if (msg.type === "handoff") {
                return (
                  <div key={idx} className={classes.ticketCard}>
                    <div className={classes.ticketRef}>
                      Ticket {msg.ticketRef}
                    </div>
                    <div>{msg.text}</div>
                  </div>
                );
              }

              if (msg.type === "status") {
                return (
                  <React.Fragment key={idx}>
                    <div className={classes.statusCard}>
                      {renderText(msg.text)}
                      {msg.magicLinkUrl && (
                        <a
                          href={msg.magicLinkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.magicLinkBtn}
                        >
                          Login to AssessHub
                        </a>
                      )}
                    </div>
                    {msg.quickReplies && msg.quickReplies.length > 0 && (
                      <div className={classes.quickReplies}>
                        {msg.quickReplies.map((qr, qrIdx) => (
                          <button
                            key={qrIdx}
                            className={classes.quickReplyBtn}
                            onClick={() => handleQuickReply(qr)}
                          >
                            {qr}
                          </button>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={idx}>
                  <div className={classes.msgBot}>{renderText(msg.text)}</div>
                  {msg.quickReplies && msg.quickReplies.length > 0 && (
                    <div className={classes.quickReplies}>
                      {msg.quickReplies.map((qr, qrIdx) => (
                        <button
                          key={qrIdx}
                          className={classes.quickReplyBtn}
                          onClick={() => handleQuickReply(qr)}
                        >
                          {qr}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Typing indicator */}
            {isLoading && (
              <div className={classes.typing}>Typing...</div>
            )}

            {/* Feedback */}
            {showFeedback && !feedbackSent && (
              <div className={classes.feedbackArea}>
                <div style={{ fontSize: 13, color: "#555" }}>
                  Was this helpful? Rate your experience:
                </div>
                <div className={classes.starRow}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`${classes.star} ${
                        star <= feedbackRating ? classes.starActive : ""
                      }`}
                      onClick={() => submitFeedback(star)}
                      aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            )}

            {feedbackSent && (
              <div className={classes.msgBot}>
                Thank you for your feedback!
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className={classes.inputArea}>
            <input
              ref={inputRef}
              className={classes.inputField}
              type="text"
              placeholder="Type your message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              className={classes.sendBtn}
              onClick={handleSend}
              disabled={isLoading || !inputText.trim()}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
